// @flow
import get from 'lodash/get';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import { createReducer } from 'redux-act';
import { createSelector } from 'reselect';
import type { OutputSelector } from 'reselect';

import { createResource } from '@gandi/resource/redux';

import type { Resource } from '@gandi/resource/redux';

import type { FullState } from '../../types';

export type LanguageFeature = {
  label: string,
  name: string,
  ...
};
export type DatabaseFeature = {
  label: string,
  name: string,
  ...
};
export type InstanceType = {
  database: { name: string, name_label: string, version: string, ... },
  id: string,
  language: { name: string, name_label: string, version: string, ... },
  product_type: string,
  recommended: boolean,
  single_application: boolean,
  ...
};

export type SelectorFilterProps = {
  database?: ?string,
  databaseVersion?: ?string,
  language?: ?string,
  languageVersion?: ?string,
  ...
};

export type InstanceTypeState = Array<InstanceType>;

// ----------------------------------------------------------------------------
// Define resource
// ----------------------------------------------------------------------------
export const resource: Resource<> = createResource('types');

// ----------------------------------------------------------------------------
// Define reducer
// ----------------------------------------------------------------------------
export default (createReducer(
  {
    [resource.fetch.success.getType()]: (state, { response }) => response.data,
  },
  [],
): {
  (): (state: Array<any>, payload?: mixed, meta?: mixed) => Array<any>,
  assignAll: ({ ... } | Array<mixed>, { ... } | Array<mixed>) => mixed,
  batch: ({ ... } | Array<mixed>) => mixed,
  bindAll: ({ ... } | Array<mixed>, { ... } | Array<mixed>) => mixed,
  disbatch: (mixed, void | Array<mixed>) => mixed,
  has: (actionCreator: mixed) => boolean,
  // $FlowFixMe[cannot-resolve-name]
  off: Reducer$Off,
  // $FlowFixMe[cannot-resolve-name]
  on: Reducer$On<Array<any>>,
  options: () => mixed,
  ...
});

// ----------------------------------------------------------------------------
// Define selectors
// ----------------------------------------------------------------------------
export const selectIsLoaded = (state: FullState): boolean =>
  resource.selectIsLoaded(state, 'default');
export const selectIsFetching = (state: FullState): boolean =>
  resource.selectIsFetching(state, 'default');
export const selectIsFailure = (state: FullState): boolean =>
  resource.selectIsFailure(state, 'default');
export const selectIsToRefetch = (state: FullState): boolean =>
  resource.selectIsToRefetch(state, 'default');

export const selectShouldFetch: OutputSelector<FullState, mixed, boolean> =
  createSelector(
    [selectIsLoaded, selectIsFetching, selectIsFailure, selectIsToRefetch],
    (isLoaded, isFetching, isFailure, isToRefetch) =>
      !isFetching && (!isLoaded || isToRefetch),
  );

// Retreive raw state for instance types
export const selectRaw: OutputSelector<FullState, any, InstanceTypeState> =
  createSelector(
    [(state: FullState) => get(state, 'simplehosting.types', [])],
    types => [...types],
  );

const normalizeName = (name: string) => name.toLowerCase().replace('.', '');
const normalizeFeature = (feature: LanguageFeature | DatabaseFeature) =>
  normalizeName(get(feature || {}, 'name', ''));
// For now, API does not provide a sort order for types.
// We want PHP and NodeJS to always be firsts.
const orderAvailableLanguages = (
  param1: DatabaseFeature,
  param2: DatabaseFeature,
): number => {
  const a = normalizeFeature(param1);
  const b = normalizeFeature(param2);
  // php is always first
  if (a === 'php') return -1;
  if (b === 'php') return 1;
  // NodeJS is always second
  if (a === 'nodejs') return -1;
  if (b === 'nodejs') return 1;
  // Python is always third
  if (a === 'python') return -1;
  if (b === 'python') return 1;
  // keep in current API order.
  return 0;
};
// For now, API does not provide a sort order for types.
// We want MySQL, then mongoDB to always be firsts.
const orderAvailableDatabases = (
  param1: DatabaseFeature,
  param2: DatabaseFeature,
): number => {
  const a = normalizeFeature(param1);
  const b = normalizeFeature(param2);
  // php is always first
  if (a === 'mysql') return -1;
  if (b === 'mysql') return 1;
  // NodeJS is always second
  if (a === 'mongodb') return -1;
  if (b === 'mongodb') return 1;
  // keep in current API order.
  return 0;
};

const uniqByName = (
  features: Array<LanguageFeature | DatabaseFeature>,
): Array<LanguageFeature | DatabaseFeature> => uniqBy(features, x => x.name);

// Retrieve the available product types available #FIXME: Not sure if needed
export const selectAvailableProductTypes = (state: FullState): Array<string> =>
  uniq(selectRaw(state).map(type => type.product_type));

// select filter from given selector props
export const selectFilterFromProps: OutputSelector<
  any,
  SelectorFilterProps,
  SelectorFilterProps,
> = createSelector([(_, props) => props], filter =>
  filter ? { ...filter } : {},
);

// Retrieve all types matching a SelectorFilterProps.
export const selectTypesFilteredBy: OutputSelector<
  FullState,
  any,
  Array<InstanceType>,
> = createSelector([selectRaw, selectFilterFromProps], (raw, filter) =>
  raw.filter(type => {
    if (!filter || !Object.keys(filter).length) return true;

    // filter on every prop
    const { language, database, languageVersion, databaseVersion } = filter;
    if (
      language &&
      normalizeName(get(type, 'language.name')) !== normalizeName(language)
    ) {
      return false;
    }
    if (
      database &&
      normalizeName(get(type, 'database.name')) !== normalizeName(database)
    ) {
      return false;
    }
    if (languageVersion && get(type, 'language.version') !== languageVersion)
      return false;
    if (databaseVersion && get(type, 'database.version') !== databaseVersion)
      return false;

    return true;
  }),
);

// retreive the available languages among all available types
export const selectAvailableLanguages: OutputSelector<
  FullState,
  ?SelectorFilterProps,
  Array<LanguageFeature>,
> = createSelector([selectTypesFilteredBy], filteredTypes => {
  return uniqByName(
    filteredTypes
      .map(type => ({
        name: get(type, 'language.name', ''),
        label: get(type, 'language.name_label', get(type, 'language.name')),
      }))
      .filter(language => language.name && language.label)
      .sort(orderAvailableLanguages),
  );
});

// retreive the available databases among all available types
export const selectAvailableDatabases: OutputSelector<
  FullState,
  ?SelectorFilterProps,
  Array<DatabaseFeature>,
> = createSelector([selectTypesFilteredBy], filteredTypes => {
  return uniqByName(
    filteredTypes
      .map(type => ({
        name: get(type, 'database.name', ''),
        label: get(type, 'database.name_label', get(type, 'database.name')),
      }))
      .filter(database => database.name && database.label)
      .sort(orderAvailableDatabases),
  );
});

export const selectAvailableFeatures = (
  state: FullState,
  props: ?SelectorFilterProps,
): Array<LanguageFeature | DatabaseFeature> => [
  ...selectAvailableLanguages(state, props),
  ...selectAvailableDatabases(state, props),
];

// Retrieve the type best matching a database and language name
// For now, we keep order brom API and pick first match,
// but we might be able to do better.
export const selectBestMatch: OutputSelector<
  FullState,
  SelectorFilterProps,
  ?InstanceType,
> = createSelector(
  [selectTypesFilteredBy],
  (matches: Array<InstanceType>): ?InstanceType => matches[0],
);

export const selectRecommendedFeatures = (
  state: FullState,
  props: ?SelectorFilterProps,
): Array<InstanceType> =>
  selectTypesFilteredBy(state, props).filter(type => type.recommended);

export const selectRecommendedTypeFilteredBy: OutputSelector<
  FullState,
  SelectorFilterProps,
  ?InstanceType,
> = createSelector(
  [selectRecommendedFeatures],
  (types: Array<InstanceType>): ?InstanceType => types[0],
);

// ----------------------------------------------------------------------------
// Define actions
// ----------------------------------------------------------------------------

export const fetchAll = (): Promise<mixed> =>
  // $FlowFixMe[incompatible-return]
  resource.fetch(
    '/simplehosting/types?status=current&status=beta&per_page=200',
  );

export const abort = (): any =>
  // $FlowFixMe[prop-missing]
  resource.fetch.failure({ err: new Error('Aborted') });
